'use client'

import { useParams, useSearchParams } from 'react-router-dom'
import { getAvatarAssets, type AvatarKey } from './config/avatars'
import { ChatAvatar } from './ChatAvatar'
import { useIsMobile } from '@/hooks/useIsMobile'
import { getVarsFromSearchParams } from './utils'
import { useCallback, useRef } from 'react'
import { TextLikeMessage } from '@/ug-core/types'
import toast from 'react-hot-toast'

export default function AvatarPlaygroundPage() {
  const { componentId } = useParams()
  let [searchParams] = useSearchParams()
  const avatarKey = (searchParams.get('avatar') || 'robot') as AvatarKey
  const personaId = searchParams.get('persona_id')
  const imageFrame = searchParams.get('imageFrame')
  const isEnabledSubtitles = searchParams.get('isEnabledSubtitles') !== 'false'
  const isMobile = useIsMobile()

  const textBuffersRef = useRef<Map<string, string>>(new Map())

  const handleTextMessage = useCallback((message: TextLikeMessage) => {
    if (message.source_type !== 'generate_text') return

    const sourceId = message.source_id || 'default'

    if (message.type === 'text') {
      const prev = textBuffersRef.current.get(sourceId) || ''
      const next = prev + (message.text || '')
      textBuffersRef.current.set(sourceId, next)
      return
    }

    if (message.type === 'text_complete') {
      const fullText = (textBuffersRef.current.get(sourceId) || '').trim()
      textBuffersRef.current.delete(sourceId)
      if (!fullText) return

      // Clean up common hallucination wrappers like code fences/backticks
      const stripFences = (input: string): string => {
        let text = input.trim()
        // Remove leading/trailing triple backtick blocks
        if (text.startsWith('```')) {
          text = text.replace(/^```[a-zA-Z0-9]*\n?/, '')
        }
        if (text.endsWith('```')) {
          text = text.replace(/```\s*$/m, '')
        }
        // Remove single backtick wrapping
        if (text.startsWith('`') && text.endsWith('`')) {
          text = text.slice(1, -1)
        }
        return text.trim()
      }

      const extractFirstUrl = (input: string): string | null => {
        const cleaned = stripFences(input)
        const httpIndex = cleaned.indexOf('http://')
        const httpsIndex = cleaned.indexOf('https://')
        const start = httpsIndex !== -1 ? httpsIndex : httpIndex
        if (start === -1) return null
        let candidate = cleaned.slice(start)
        // Cut off at a closing fence/backtick if present
        const fenceIdx = candidate.indexOf('```')
        if (fenceIdx !== -1) candidate = candidate.slice(0, fenceIdx)
        const backtickIdx = candidate.indexOf('`')
        if (backtickIdx !== -1) candidate = candidate.slice(0, backtickIdx)
        // Trim surrounding whitespace and common trailing punctuation
        candidate = candidate.trim().replace(/[)>\],;:.]+$/, '')
        return candidate
      }

      const sanitizeUrl = (raw: string): string | null => {
        // Try as-is first
        try {
          const parsed = new URL(raw)
          if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
            return parsed.toString()
          }
        } catch (_) {}
        // Attempt to encode the query string if present
        try {
          const qIndex = raw.indexOf('?')
          if (qIndex !== -1) {
            const base = raw.slice(0, qIndex)
            const query = raw.slice(qIndex + 1)
            const encoded = query
              .split('&')
              .map((pair) => {
                const [k, ...rest] = pair.split('=')
                const v = rest.join('=')
                if (!k) return ''
                return `${k}=${encodeURIComponent(v)}`
              })
              .filter(Boolean)
              .join('&')
            const rebuilt = `${base}?${encoded}`
            const parsed = new URL(rebuilt)
            if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
              return parsed.toString()
            }
          }
        } catch (_) {}
        // Last resort: replace spaces with %20
        try {
          const fallback = raw.replace(/\s+/g, '%20')
          const parsed = new URL(fallback)
          if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
            return parsed.toString()
          }
        } catch (_) {}
        return null
      }

      const candidateRaw = extractFirstUrl(fullText)
      if (!candidateRaw) return

      const url = sanitizeUrl(candidateRaw)
      if (!url) return

      let secondsRemaining = 3
      const toastId = 'redirect-countdown'
      toast.loading(`Redirecting to URL in ${secondsRemaining}s...`, { id: toastId })
      const intervalId = setInterval(() => {
        secondsRemaining -= 1
        if (secondsRemaining > 0) {
          toast.loading(`Redirecting to URL in ${secondsRemaining}s...`, { id: toastId })
        } else {
          clearInterval(intervalId)
          toast.success('Redirecting to URL...', { id: toastId })
          window.location.href = url!
        }
      }, 1000)
    }
  }, [])

  try {
    const avatarAssets = getAvatarAssets(avatarKey)
    const contextValues = getVarsFromSearchParams(searchParams)

    return (
      <div
        className={`min-h-screen ${isMobile ? 'w-screen flex flex-col items-center justify-between p-0 m-0' : 'w-full'}`}
      >
        <ChatAvatar
          componentId={componentId!}
          personaId={personaId || undefined}
          contextValues={contextValues}
          avatarAssets={avatarAssets}
          imageFrame={imageFrame || undefined}
          isEnabledSubtitles={isEnabledSubtitles}
          onTextMessage={handleTextMessage}
        />
      </div>
    )
  } catch (error) {
    return <div className="p-4 text-red-500">Could not find avatar: {avatarKey}</div>
  }
}
  